'use client';
import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import {
  HelpCircle,
  Search,
  ChevronDown,
  ChevronUp,
  Sparkles,
  MessageSquare,
  CreditCard,
  Image,
  Palette,
  PlayCircle,
} from 'lucide-react';
import { resetTour } from '@/components/OnboardingTour';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';
import { interpolateSiteContact } from '@/lib/siteContact';

type FaqCategoryKey = 'general' | 'billing' | 'credits' | 'imageGeneration' | 'svg';

interface LocalizedFaq {
  category: FaqCategoryKey;
  q: string;
  a: string;
}

const CATEGORY_ORDER: FaqCategoryKey[] = [
  'general',
  'billing',
  'credits',
  'imageGeneration',
  'svg',
];

const categoryIcons: Record<FaqCategoryKey, React.ElementType> = {
  general: HelpCircle,
  billing: CreditCard,
  credits: Sparkles,
  imageGeneration: Image,
  svg: Palette,
};

export default function HelpCenterContent() {
  const { t } = useLanguage();
  const hc = t?.helpCenterPage;
  const cats = hc?.categories;

  const categoryLabels = useMemo(
    () => ({
      general: cats?.general ?? 'General',
      billing: cats?.billing ?? 'Billing',
      credits: cats?.credits ?? 'Credits',
      imageGeneration: cats?.imageGeneration ?? 'Image Generation',
      svg: cats?.svg ?? 'SVG',
    }),
    [cats],
  );

  const faqs: LocalizedFaq[] = useMemo(() => {
    const items = hc?.faqs ?? [];
    return items.map((item) => ({
      category: item.category as FaqCategoryKey,
      q: item.q,
      a: item.a,
    }));
  }, [hc?.faqs]);

  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const resolveAnswer = (answer: string) => {
    if (answer === '__REFUND__') {
      return interpolateSiteContact(
        t?.helpCenter?.refundAnswer ??
          'Refund requests must be submitted within 24 hours of purchase and are only available if fewer than 5% of your monthly AI credits have been used and no significant AI-generated assets have been created or exported. Because AI and provider processing costs are incurred immediately, consumed credits and services are generally non-refundable. To request a refund, contact us at {supportEmail} with your account email and the reason for your request.',
      );
    }
    return answer;
  };

  const getCategoryLabel = (key: FaqCategoryKey) => categoryLabels[key] ?? key;

  const filtered = faqs.filter((faq) => {
    const matchCat =
      activeCategory === 'all' || faq.category === (activeCategory as FaqCategoryKey);
    const resolved = resolveAnswer(faq.a);
    const label = getCategoryLabel(faq.category);
    const matchSearch =
      search === '' ||
      faq.q.toLowerCase().includes(search.toLowerCase()) ||
      resolved.toLowerCase().includes(search.toLowerCase()) ||
      label.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const handleRestartTour = () => {
    resetTour();
    window.dispatchEvent(new CustomEvent('aimaker-restart-tour'));
    toast.success(hc?.tourRestarted ?? 'Tour restarted! Navigate to the main page to see it.');
  };

  const articlesCountLabel = (count: number) =>
    (hc?.articlesCount ?? '{count} articles').replace('{count}', String(count));

  const categoryKeysForCards = CATEGORY_ORDER;

  return (
    <main className="min-h-screen bg-background pt-24 pb-20">
      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-12">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <HelpCircle size={13} className="text-primary" />
            <span className="text-xs font-600 text-primary">{hc?.badge ?? 'Help Center'}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-800 text-foreground mb-5 leading-tight">
            {hc?.title ?? 'How Can We'}{' '}
            <span className="gradient-text">{hc?.titleHighlight ?? 'Help You?'}</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed mb-6">
            {hc?.subtitle ??
              'Find answers to common questions about AIMaker Studio — from getting started and using AI tools to billing, credits, and account management.'}
          </p>
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              placeholder={hc?.searchPlaceholder ?? 'Search help articles...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-muted border border-border rounded-xl pl-10 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
            />
          </div>
        </div>
      </section>

      {activeCategory === 'all' && search === '' && (
        <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-10">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {categoryKeysForCards.map((catKey) => {
              const CatIcon = categoryIcons[catKey] || HelpCircle;
              const count = faqs.filter((f) => f.category === catKey).length;
              const label = getCategoryLabel(catKey);
              return (
                <button
                  key={catKey}
                  onClick={() => setActiveCategory(catKey)}
                  className="glass-card rounded-xl p-4 border border-border text-left hover:border-primary/30 transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                    <CatIcon size={15} className="text-primary" />
                  </div>
                  <p className="text-xs font-700 text-foreground mb-0.5">{label}</p>
                  <p className="text-xs text-muted-foreground">{articlesCountLabel(count)}</p>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-8">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveCategory('all')}
            className={`px-4 py-1.5 rounded-full text-sm font-500 border transition-colors ${
              activeCategory === 'all' ?'bg-primary text-primary-foreground border-primary' :'bg-muted border-border text-muted-foreground hover:text-foreground hover:border-primary/30'
            }`}
          >
            {hc?.categoryAll ?? 'All'}
          </button>
          {categoryKeysForCards.map((catKey) => (
            <button
              key={catKey}
              onClick={() => setActiveCategory(catKey)}
              className={`px-4 py-1.5 rounded-full text-sm font-500 border transition-colors ${
                activeCategory === catKey
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted border-border text-muted-foreground hover:text-foreground hover:border-primary/30'
              }`}
            >
              {getCategoryLabel(catKey)}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-16">
        {filtered.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {hc?.noResults ?? 'No results found. Try a different search term or category.'}
          </p>
        ) : (
          <div className="flex flex-col gap-3 max-w-3xl">
            {filtered.map((faq, i) => (
              <div
                key={`faq-${faq.category}-${faq.q}`}
                className="glass-card rounded-xl border border-border overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left"
                >
                  <div>
                    <span className="text-xs font-600 text-primary uppercase tracking-widest block mb-0.5">
                      {getCategoryLabel(faq.category)}
                    </span>
                    <span className="text-sm font-600 text-foreground">{faq.q}</span>
                  </div>
                  {openIndex === i ? (
                    <ChevronUp size={16} className="text-muted-foreground shrink-0 ml-4" />
                  ) : (
                    <ChevronDown size={16} className="text-muted-foreground shrink-0 ml-4" />
                  )}
                </button>
                {openIndex === i && (
                  <div className="px-6 pb-5 text-sm text-muted-foreground leading-relaxed border-t border-border/50 pt-4">
                    {resolveAnswer(faq.a)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-screen-xl mx-auto px-6 lg:px-10">
        <div className="rounded-2xl bg-muted/50 border border-border p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={16} className="text-primary" />
              <h2 className="text-base font-700 text-foreground">
                {hc?.stillNeedHelpTitle ?? 'Still need help?'}
              </h2>
            </div>
            <p className="text-sm text-muted-foreground">
              {hc?.stillNeedHelpDesc ??
                "Can't find what you're looking for? Our support team is happy to help."}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 shrink-0">
            <button
              onClick={handleRestartTour}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm border border-primary/30 text-primary hover:bg-primary/10 transition-all font-600"
            >
              <PlayCircle size={14} />
              {hc?.restartTour ?? 'Restart Guided Tour'}
            </button>
            <Link
              href="/contact"
              className="btn-primary px-5 py-2.5 rounded-xl text-sm flex items-center gap-2"
            >
              <MessageSquare size={14} /> {hc?.contactSupport ?? 'Contact Support'}
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
